/**
 * Turn page drawings into one SVG per figure, then attach each figure to a question.
 *
 * A test passes hand-written drawings. It does not need a PDF or pdf.js.
 * The PDF reader is the only producer of these drawings.
 *
 * @format
 */

/** A rectangle in PDF user space. The origin is the bottom-left of the page. */
export interface FigureBox {
	x: number;
	y: number;
	width: number;
	height: number;
}

export type PathCommand =
	| { op: "M"; x: number; y: number }
	| { op: "L"; x: number; y: number }
	| { op: "C"; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
	| { op: "Z" };

export interface VectorDrawing {
	kind: "path";
	box: FigureBox;
	commands: PathCommand[];
	stroke?: string;
	fill?: string;
	fillRule?: "nonzero" | "evenodd";
	lineWidth: number;
}

export interface ImageDrawing {
	kind: "image";
	box: FigureBox;
	/** Pixel width of the embedded image, before the page scales it. */
	intrinsicWidth: number;
	/** Pixel height of the embedded image, before the page scales it. */
	intrinsicHeight: number;
	href: string;
}

export type PageDrawing = VectorDrawing | ImageDrawing;

export interface FigurePage {
	pageNumber: number;
	pageWidth: number;
	pageHeight: number;
	drawings: PageDrawing[];
}

export interface ClusteredFigure {
	page: number;
	x: number;
	y: number;
	width: number;
	height: number;
	svg: string;
}

export interface FigureLabel {
	questionNumber: string;
	page: number;
	/** Baseline of the label. Null when the page is known and the y is not. */
	y: number | null;
}

/** One figure's stored keys: the SVG, and the backup raster when one was made. */
export interface FigureAssetKeys {
	svg: string;
	raster?: string;
}

export interface AssetRef {
	/** English SVG key (the recommended asset). */
	en?: string;
	/** Welsh SVG key. */
	cy?: string;
	/** English backup raster key. */
	enRaster?: string;
	/** Welsh backup raster key. */
	cyRaster?: string;
}

export interface AssetManifest {
	questions: {
		questionNumber: string;
		paper: AssetRef[];
		markScheme: AssetRef[];
	}[];
}

const HAIRLINE = 3;
const SEED_SHORT = 36;
const SEED_LONG = 72;
const CLUSTER_GAP = 12;
const BACKGROUND_SHARE = 0.92;
const IMAGE_MIN_PX = 200;
const MAX_PIXELS = 8_000_000;
const LABEL_SLACK = 8;
/**
 * How far a label may sit from an image and still belong to it, in PDF points.
 * One caption line fits. A question line further out stays out.
 */
export const ASSET_NEAR_MARGIN = 32;

/** A pixel rectangle in a rendered page image. The origin is the top-left. */
export interface PixelRect {
	left: number;
	top: number;
	width: number;
	height: number;
}

/**
 * Convert a figure box (PDF user space, origin bottom-left, y up) to a pixel
 * crop rectangle in a page rendered at {@link scale} (origin top-left, y down).
 * The rectangle is clamped to the rendered page so a crop never reads outside it.
 */
export const figureBoxToPixelRect = (box: FigureBox, pageHeight: number, scale: number): PixelRect => {
	const left = Math.max(0, Math.round(box.x * scale));
	const top = Math.max(0, Math.round((pageHeight - (box.y + box.height)) * scale));
	const width = Math.max(1, Math.round(box.width * scale));
	const height = Math.max(1, Math.round(box.height * scale));
	return { left, top, width, height };
};

/**
 * Grow a figure box towards a clamp rectangle (the content frame, or the page).
 * Each side grows by a percentage of its own gap to the frame, so a 100 fills
 * all the way to the frame on that side and never crosses it. The grow is
 * independent per side, so an off-centre figure uses the room it has.
 */
export const padBoxToFrame = (box: FigureBox, frame: FigureBox, pctX: number, pctY: number): FigureBox => {
	const clampX = Math.max(0, Math.min(100, pctX)) / 100;
	const clampY = Math.max(0, Math.min(100, pctY)) / 100;
	const gapLeft = Math.max(0, box.x - frame.x);
	const gapRight = Math.max(0, frame.x + frame.width - (box.x + box.width));
	const gapBottom = Math.max(0, box.y - frame.y);
	const gapTop = Math.max(0, frame.y + frame.height - (box.y + box.height));
	const left = box.x - gapLeft * clampX;
	const bottom = box.y - gapBottom * clampY;
	return {
		x: left,
		y: bottom,
		width: box.width + (gapLeft + gapRight) * clampX,
		height: box.height + (gapBottom + gapTop) * clampY,
	};
};

/**
 * Trim a padded crop to the content inside it: the union of the figure box and
 * the kept word boxes, grown by a small margin, then intersected with the
 * padded box. White space and any item outside that union fall away, so a crop
 * keeps only the figure and the words used to build it. With no word boxes the
 * trim is the figure box alone.
 *
 * `reach` limits how far a word box may pull the crop from the figure box. A
 * caption that sits further than `reach` from the figure is left out of the
 * trim, so a crop does not balloon to a distant label and leave white space.
 * The word stays attributed to the figure elsewhere; only the trim ignores it.
 * Omit `reach` (or pass 0) to include every word box.
 */
export const trimBoxToContent = (
	paddedBox: FigureBox,
	figureBox: FigureBox,
	wordBoxes: readonly FigureBox[],
	marginX: number,
	marginY: number,
	reach = 0,
): FigureBox => {
	const gapTo = (box: FigureBox): number => {
		const gapX =
			box.x > figureBox.x + figureBox.width
				? box.x - (figureBox.x + figureBox.width)
				: figureBox.x > box.x + box.width
					? figureBox.x - (box.x + box.width)
					: 0;
		const gapY =
			box.y > figureBox.y + figureBox.height
				? box.y - (figureBox.y + figureBox.height)
				: figureBox.y > box.y + box.height
					? figureBox.y - (box.y + box.height)
					: 0;
		return Math.max(gapX, gapY);
	};
	let minX = figureBox.x;
	let minY = figureBox.y;
	let maxX = figureBox.x + figureBox.width;
	let maxY = figureBox.y + figureBox.height;
	for (const box of wordBoxes) {
		if (reach > 0 && gapTo(box) > reach) {
			continue;
		}
		minX = Math.min(minX, box.x);
		minY = Math.min(minY, box.y);
		maxX = Math.max(maxX, box.x + box.width);
		maxY = Math.max(maxY, box.y + box.height);
	}
	minX -= marginX;
	maxX += marginX;
	minY -= marginY;
	maxY += marginY;
	// Clamp the content union to the padded box: never grow past the ceiling.
	const left = Math.max(paddedBox.x, minX);
	const bottom = Math.max(paddedBox.y, minY);
	const right = Math.min(paddedBox.x + paddedBox.width, maxX);
	const top = Math.min(paddedBox.y + paddedBox.height, maxY);
	return { x: left, y: bottom, width: Math.max(1, right - left), height: Math.max(1, top - bottom) };
};

/** The largest glyph box we thicken, in points. A real filled shape is larger. */
const GLYPH_MAX_SIDE = 16;
/** The stroke we add to a thin glyph, in points. */
const GLYPH_STROKE = 0.4;

/** Knobs for the figure crop. Omit them and the code defaults apply. */
export interface FigureClusterTune {
	hairline: number;
	seedShort: number;
	seedLong: number;
	clusterGap: number;
	imageMinPx: number;
	nearMargin: number;
	/** The largest glyph box to thicken, in points. */
	glyphMaxSide: number;
	/** The stroke added to a thin glyph, in points. */
	glyphStroke: number;
}

export const DEFAULT_FIGURE_CLUSTER_TUNE: FigureClusterTune = {
	hairline: HAIRLINE,
	seedShort: SEED_SHORT,
	seedLong: SEED_LONG,
	clusterGap: CLUSTER_GAP,
	imageMinPx: IMAGE_MIN_PX,
	nearMargin: ASSET_NEAR_MARGIN,
	glyphMaxSide: GLYPH_MAX_SIDE,
	glyphStroke: GLYPH_STROKE,
};
/**
 * How far an undersized part may sit from a real figure in the same caption
 * band and still rejoin it, in PDF points. A reaction product that a small gap
 * split off sits right beside its figure. A page-footer barcode sits far below
 * every figure, so it stays out. See {@link mergeFiguresByCaption}.
 */
const PART_REJOIN_GAP = 40;

/** A caption may sit just above the drawing it names, or a short way to its left. */
const CAPTION_SLACK = 120;
const CAPTION_X_BEFORE = 100;
const CAPTION_X_AFTER = 24;

/** Same join key as the mark-scheme extractor. A space or a trailing dot does not change the number. */
const normaliseAssetNumber = (value: string): string =>
	value
		.toLowerCase()
		.replace(/^q(?:uestion)?\s*/, "")
		.replace(/(\d)\s*[.)]/g, "$1")
		.replace(/[\s.]/g, "");

const formatNumber = (value: number): string => {
	const rounded = Math.round(value * 100) / 100;
	if (Object.is(rounded, -0) || Object.is(rounded, 0)) {
		return "0";
	}
	return String(rounded);
};

const escapeAttr = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const isSafeHref = (href: string): boolean =>
	href.startsWith("data:image/png;base64,") || href.startsWith("data:image/jpeg;base64,");

const unionBox = (left: FigureBox, right: FigureBox): FigureBox => {
	const x = Math.min(left.x, right.x);
	const y = Math.min(left.y, right.y);
	const maxX = Math.max(left.x + left.width, right.x + right.width);
	const maxY = Math.max(left.y + left.height, right.y + right.height);
	return { x, y, width: maxX - x, height: maxY - y };
};

const overlapsBox = (left: FigureBox, right: FigureBox): boolean =>
	left.x < right.x + right.width &&
	right.x < left.x + left.width &&
	left.y < right.y + right.height &&
	right.y < left.y + left.height;

const withinGap = (left: FigureBox, right: FigureBox, clusterGap: number): boolean => {
	const isSeparatedX = left.x + left.width + clusterGap < right.x || right.x + right.width + clusterGap < left.x;
	const isSeparatedY = left.y + left.height + clusterGap < right.y || right.y + right.height + clusterGap < left.y;
	return !isSeparatedX && !isSeparatedY;
};

const isBackground = (box: FigureBox, page: FigurePage): boolean =>
	box.width > page.pageWidth * BACKGROUND_SHARE && box.height > page.pageHeight * BACKGROUND_SHARE;

/** The ruled border around a WJEC page. It is not a diagram. */
const isPageFrame = (drawing: PageDrawing, page: FigurePage): boolean => {
	if (drawing.kind !== "path" || drawing.lineWidth > 1) {
		return false;
	}
	const right = page.pageWidth - (drawing.box.x + drawing.box.width);
	const top = page.pageHeight - (drawing.box.y + drawing.box.height);
	return (
		drawing.box.x < 100 &&
		drawing.box.y < 120 &&
		right < 100 &&
		top < 120 &&
		drawing.box.width > page.pageWidth * 0.7 &&
		drawing.box.height > page.pageHeight * 0.7
	);
};

const isHairline = (box: FigureBox, hairline: number): boolean => Math.min(box.width, box.height) <= hairline;

/**
 * A thin ruled bar hard against a page edge, running most of the page — the
 * "Examiner only" marking column divider, or a full-height margin rule. It is
 * furniture, not a figure. A real figure is never a 14pt-wide, full-height bar
 * sitting on the page margin, so a thin bar that spans most of a page dimension
 * and touches that edge is dropped before clustering.
 */
const MARGIN_BAR_THIN = 20;
const MARGIN_BAR_SPAN = 0.85;
const MARGIN_BAR_EDGE = 20;
const isMarginBar = (drawing: PageDrawing, page: FigurePage): boolean => {
	if (drawing.kind !== "path") {
		return false;
	}
	const { x, y, width, height } = drawing.box;
	const right = page.pageWidth - (x + width);
	const top = page.pageHeight - (y + height);
	// A tall thin bar against the left or right edge.
	const isVerticalBar =
		width <= MARGIN_BAR_THIN &&
		height >= page.pageHeight * MARGIN_BAR_SPAN &&
		(x <= MARGIN_BAR_EDGE || right <= MARGIN_BAR_EDGE);
	// A wide thin bar against the top or bottom edge.
	const isHorizontalBar =
		height <= MARGIN_BAR_THIN &&
		width >= page.pageWidth * MARGIN_BAR_SPAN &&
		(y <= MARGIN_BAR_EDGE || top <= MARGIN_BAR_EDGE);
	return isVerticalBar || isHorizontalBar;
};

/**
 * The content frame of a page: the inner rectangle of the WJEC ruled border.
 * A crop may grow up to this rectangle but not past it, so padding never
 * swallows the border. A page with no detectable frame returns the page
 * rectangle, so the fallback is the page edge.
 */
export const contentFrameFor = (page: FigurePage): FigureBox => {
	for (const drawing of page.drawings) {
		if (isPageFrame(drawing, page)) {
			return { ...drawing.box };
		}
	}
	return { x: 0, y: 0, width: page.pageWidth, height: page.pageHeight };
};

const isFigureSized = (box: FigureBox, tune: FigureClusterTune): boolean => {
	const shortSide = Math.min(box.width, box.height);
	const longSide = Math.max(box.width, box.height);
	return shortSide >= tune.seedShort && longSide >= tune.seedLong;
};

const isImageSeed = (drawing: ImageDrawing, tune: FigureClusterTune): boolean => {
	if (drawing.intrinsicWidth < tune.imageMinPx || drawing.intrinsicHeight < tune.imageMinPx) {
		return false;
	}
	if (drawing.intrinsicWidth * drawing.intrinsicHeight > MAX_PIXELS) {
		return false;
	}
	if (isHairline(drawing.box, tune.hairline) || !isSafeHref(drawing.href)) {
		return false;
	}
	return true;
};

const svgPoint = (figure: FigureBox, x: number, y: number): string => {
	const svgX = x - figure.x;
	const svgY = figure.y + figure.height - y;
	return `${formatNumber(svgX)} ${formatNumber(svgY)}`;
};

const commandToPath = (figure: FigureBox, command: PathCommand): string => {
	if (command.op === "M" || command.op === "L") {
		return `${command.op} ${svgPoint(figure, command.x, command.y)}`;
	}
	if (command.op === "Z") {
		return "Z";
	}
	return `C ${svgPoint(figure, command.x1, command.y1)} ${svgPoint(figure, command.x2, command.y2)} ${svgPoint(figure, command.x, command.y)}`;
};

const drawingElement = (figure: FigureBox, drawing: PageDrawing): string | undefined => {
	if (drawing.kind === "image") {
		if (!isSafeHref(drawing.href)) {
			return undefined;
		}
		const x = drawing.box.x - figure.x;
		const y = figure.y + figure.height - (drawing.box.y + drawing.box.height);
		return `<image href="${escapeAttr(drawing.href)}" x="${formatNumber(x)}" y="${formatNumber(y)}" width="${formatNumber(drawing.box.width)}" height="${formatNumber(drawing.box.height)}"/>`;
	}
	const path = drawing.commands.map((command) => commandToPath(figure, command)).join(" ");
	if (path.trim() === "") {
		return undefined;
	}
	const fill = drawing.fill ? escapeAttr(drawing.fill) : "none";
	const stroke = drawing.stroke ? escapeAttr(drawing.stroke) : "none";
	const rule = drawing.fillRule === "evenodd" ? ` fill-rule="evenodd"` : "";
	const width = drawing.lineWidth > 0 ? drawing.lineWidth : 1;
	return `<path d="${escapeAttr(path)}" fill="${fill}" stroke="${stroke}" stroke-width="${formatNumber(width)}"${rule}/>`;
};

/**
 * A small glyph outline — a digit on a scale readout, say — is a tiny filled
 * path with a thin or absent stroke. Scaled down into the figure viewBox it can
 * render too faint to read. {@link thickenGlyphOutlines} adds a thin stroke in
 * the fill colour so the shape reads clearly. It never moves a coordinate, so
 * the digit keeps its exact shape — a "7" cannot become a "1".
 */

const isSmallGlyph = (drawing: PageDrawing, maxSide: number, stroke: number): drawing is VectorDrawing => {
	if (drawing.kind !== "path" || !drawing.fill) {
		return false;
	}
	const { width, height } = drawing.box;
	if (width <= 0 || height <= 0) {
		return false;
	}
	if (Math.max(width, height) > maxSide) {
		return false;
	}
	// Leave a glyph that already carries a visible stroke alone.
	return !drawing.stroke || drawing.lineWidth < stroke;
};

/**
 * Give every small filled glyph a thin stroke in its own fill colour. The shape
 * is unchanged; only its weight increases, so a faint digit becomes legible.
 */
export const thickenGlyphOutlines = (
	drawings: readonly PageDrawing[],
	options: { maxSide?: number; stroke?: number } = {},
): PageDrawing[] => {
	const maxSide = options.maxSide ?? GLYPH_MAX_SIDE;
	const stroke = options.stroke ?? GLYPH_STROKE;
	return drawings.map((drawing) =>
		isSmallGlyph(drawing, maxSide, stroke)
			? { ...drawing, stroke: drawing.fill, lineWidth: Math.max(drawing.lineWidth, stroke) }
			: drawing,
	);
};

const svgFor = (drawings: readonly PageDrawing[]): string | undefined => {
	if (drawings.length === 0) {
		return undefined;
	}
	let box = drawings[0].box;
	for (const drawing of drawings.slice(1)) {
		box = unionBox(box, drawing.box);
	}
	if (box.width <= 0 || box.height <= 0) {
		return undefined;
	}
	const body = drawings
		.map((drawing) => drawingElement(box, drawing))
		.filter((element): element is string => element !== undefined)
		.join("");
	if (body.length === 0) {
		return undefined;
	}
	const width = formatNumber(box.width);
	const height = formatNumber(box.height);
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${body}</svg>`;
};

const clusterDrawings = (seeds: PageDrawing[], rules: PageDrawing[], clusterGap: number): PageDrawing[][] => {
	const parent = seeds.map((_, index) => index);
	const find = (index: number): number => {
		let cursor = index;
		while (parent[cursor] !== cursor) {
			cursor = parent[cursor];
		}
		return cursor;
	};
	for (let left = 0; left < seeds.length; left += 1) {
		for (let right = left + 1; right < seeds.length; right += 1) {
			if (!withinGap(seeds[left].box, seeds[right].box, clusterGap)) {
				continue;
			}
			const leftRoot = find(left);
			const rightRoot = find(right);
			if (leftRoot !== rightRoot) {
				parent[rightRoot] = leftRoot;
			}
		}
	}
	const groups = new Map<number, PageDrawing[]>();
	for (let index = 0; index < seeds.length; index += 1) {
		const root = find(index);
		const group = groups.get(root) ?? [];
		group.push(seeds[index]);
		groups.set(root, group);
	}
	for (const group of groups.values()) {
		const seedBox = group.reduce((union, drawing) => unionBox(union, drawing.box), group[0].box);
		for (const rule of rules) {
			if (withinGap(seedBox, rule.box, clusterGap)) {
				group.push(rule);
			}
		}
	}
	return [...groups.values()];
};

const expandBox = (box: FigureBox, nearMargin: number): FigureBox => ({
	x: box.x - nearMargin,
	y: box.y - nearMargin,
	width: box.width + nearMargin * 2,
	height: box.height + nearMargin * 2,
});

const boxGap = (left: FigureBox, right: FigureBox): number => {
	const gapX =
		left.x > right.x + right.width
			? left.x - (right.x + right.width)
			: right.x > left.x + left.width
				? right.x - (left.x + left.width)
				: 0;
	const gapY =
		left.y > right.y + right.height
			? left.y - (right.y + right.height)
			: right.y > left.y + left.height
				? right.y - (left.y + left.height)
				: 0;
	return gapX * gapX + gapY * gapY;
};

const nearestImage = (
	drawing: PageDrawing,
	images: readonly ImageDrawing[],
	nearMargin: number,
): ImageDrawing | undefined => {
	let chosen: ImageDrawing | undefined;
	let best = Number.POSITIVE_INFINITY;
	for (const image of images) {
		if (!overlapsBox(drawing.box, expandBox(image.box, nearMargin))) {
			continue;
		}
		const gap = boxGap(drawing.box, image.box);
		if (gap < best) {
			best = gap;
			chosen = image;
		}
	}
	return chosen;
};

/**
 * Paths within {@link ASSET_NEAR_MARGIN} of an image join that image.
 * A path further away stays out, even when a nearer path bridges the gap.
 * A page with no image keeps the clustered groups.
 */
const attachLabelsNearImages = (
	groups: PageDrawing[][],
	pool: readonly PageDrawing[],
	nearMargin: number,
): PageDrawing[][] => {
	const images = pool.filter((drawing): drawing is ImageDrawing => drawing.kind === "image");
	if (images.length === 0) {
		return groups;
	}
	const owner = new Map<ImageDrawing, PageDrawing[]>();
	for (const image of images) {
		owner.set(image, [image]);
	}
	const consumed = new Set<PageDrawing>(images);
	for (const drawing of pool) {
		if (drawing.kind === "image") {
			continue;
		}
		const image = nearestImage(drawing, images, nearMargin);
		if (!image) {
			continue;
		}
		owner.get(image)?.push(drawing);
		consumed.add(drawing);
	}
	const merged = groups
		.filter((group) => group.some((drawing) => drawing.kind === "image"))
		.map((group) => {
			const seen = new Set<PageDrawing>();
			const combined: PageDrawing[] = [];
			for (const drawing of group) {
				if (drawing.kind !== "image") {
					continue;
				}
				for (const item of owner.get(drawing) ?? []) {
					if (!seen.has(item)) {
						seen.add(item);
						combined.push(item);
					}
				}
			}
			return combined;
		});
	const rest = groups
		.filter((group) => group.every((drawing) => drawing.kind !== "image"))
		.map((group) => group.filter((drawing) => !consumed.has(drawing)))
		.filter((group) => group.length > 0);
	return [...merged, ...rest];
};

/**
 * Merge drawing groups whose bounding boxes substantially overlap. The
 * clustering splits a single chart into several seed groups when parts sit more
 * than the cluster gap apart, which yields several near-duplicate figures for
 * one chart. Two genuinely separate figures sit side by side and barely
 * overlap, so a high overlap threshold keeps them apart while collapsing the
 * duplicates into one group whose SVG covers the whole chart.
 *
 * Overlap is measured as the intersection area over each box's area; both must
 * exceed the threshold, so only near-duplicate groups merge (one chart split
 * into parts). A small element sitting inside a larger figure does not drag the
 * two together.
 */
export const mergeOverlappingGroups = (groups: readonly PageDrawing[][], minOverlap = 0.6): PageDrawing[][] => {
	const boxOf = (group: readonly PageDrawing[]): FigureBox =>
		group.reduce((union, drawing) => unionBox(union, drawing.box), group[0].box);
	const entries = groups.filter((group) => group.length > 0).map((group) => ({ group, box: boxOf(group) }));
	const parent = entries.map((_, index) => index);
	const find = (index: number): number => {
		let cursor = index;
		while (parent[cursor] !== cursor) {
			cursor = parent[cursor];
		}
		return cursor;
	};
	const area = (box: FigureBox): number => Math.max(0, box.width) * Math.max(0, box.height);
	const intersectionArea = (left: FigureBox, right: FigureBox): number => {
		const x = Math.max(left.x, right.x);
		const y = Math.max(left.y, right.y);
		const maxX = Math.min(left.x + left.width, right.x + right.width);
		const maxY = Math.min(left.y + left.height, right.y + right.height);
		return Math.max(0, maxX - x) * Math.max(0, maxY - y);
	};
	for (let left = 0; left < entries.length; left += 1) {
		for (let right = left + 1; right < entries.length; right += 1) {
			const leftArea = area(entries[left].box);
			const rightArea = area(entries[right].box);
			if (leftArea <= 0 || rightArea <= 0) {
				continue;
			}
			const overlap = intersectionArea(entries[left].box, entries[right].box);
			// Both boxes must be mostly covered by the overlap: true duplicates of
			// one chart, not a small element inside a larger figure.
			if (overlap / leftArea >= minOverlap && overlap / rightArea >= minOverlap) {
				parent[find(right)] = find(left);
			}
		}
	}
	const merged = new Map<number, PageDrawing[]>();
	for (let index = 0; index < entries.length; index += 1) {
		const root = find(index);
		const bucket = merged.get(root) ?? [];
		bucket.push(...entries[index].group);
		merged.set(root, bucket);
	}
	return [...merged.values()];
};

/**
 * One page's clustered output, split by size.
 *
 * `figures` are the real figures: a cluster that makes a valid SVG and is
 * figure-sized. `parts` are undersized clusters that still make a valid SVG —
 * a lone arrow, a stray reaction product, a small label group. A part is not a
 * figure on its own. The caption-merge step keeps a part only when it shares a
 * caption band with a real figure, and drops it otherwise, so a stray mark on a
 * captionless page never becomes an asset. See {@link mergeFiguresByCaption}.
 */
export interface FigurePageClusters {
	figures: ClusteredFigure[];
	parts: ClusteredFigure[];
}

/**
 * Cluster one page into real figures and undersized parts.
 * {@link clusterFigurePage} returns only the figures. The caption-merge step
 * uses the parts to rescue a fragment that a caption ties to a real figure.
 */
export const clusterFigurePageParts = (
	page: FigurePage,
	tune: FigureClusterTune = DEFAULT_FIGURE_CLUSTER_TUNE,
): FigurePageClusters => {
	const seeds: PageDrawing[] = [];
	const rules: PageDrawing[] = [];
	for (const drawing of page.drawings) {
		if (isBackground(drawing.box, page) || isPageFrame(drawing, page) || isMarginBar(drawing, page)) {
			continue;
		}
		if (drawing.kind === "image") {
			if (isImageSeed(drawing, tune)) {
				seeds.push(drawing);
			}
			continue;
		}
		if (isHairline(drawing.box, tune.hairline)) {
			rules.push(drawing);
			continue;
		}
		seeds.push(drawing);
	}
	const figures: ClusteredFigure[] = [];
	const parts: ClusteredFigure[] = [];
	const grouped = attachLabelsNearImages(
		clusterDrawings(seeds, rules, tune.clusterGap),
		[...seeds, ...rules],
		tune.nearMargin,
	);
	for (const group of mergeOverlappingGroups(grouped)) {
		const cleaned = thickenGlyphOutlines(group, { maxSide: tune.glyphMaxSide, stroke: tune.glyphStroke });
		const svg = svgFor(cleaned);
		const box = cleaned.reduce((union, drawing) => unionBox(union, drawing.box), cleaned[0].box);
		if (!svg) {
			continue;
		}
		if (!isFigureSized(box, tune)) {
			// An undersized cluster. Keep it as a part for the caption-merge to
			// judge; drop it there if no caption ties it to a real figure.
			parts.push({ page: page.pageNumber, x: box.x, y: box.y, width: box.width, height: box.height, svg });
			continue;
		}
		figures.push({ page: page.pageNumber, x: box.x, y: box.y, width: box.width, height: box.height, svg });
	}
	const sortTopDown = (list: ClusteredFigure[]): ClusteredFigure[] =>
		list.sort((left, right) => right.y - left.y || left.x - right.x);
	return { figures: sortTopDown(figures), parts: sortTopDown(parts) };
};

/** Keep a real figure. Drop answer lines, page backgrounds, and bitmaps under the minimum size. */
export const clusterFigurePage = (
	page: FigurePage,
	tune: FigureClusterTune = DEFAULT_FIGURE_CLUSTER_TUNE,
): ClusteredFigure[] => clusterFigurePageParts(page, tune).figures;

const questionNumbersFor = (figure: ClusteredFigure, labels: readonly FigureLabel[]): string[] => {
	const onPage = labels.filter((label) => label.page === figure.page);
	if (onPage.length === 0) {
		return [];
	}
	const positioned = onPage.filter((label): label is FigureLabel & { y: number } => label.y !== null);
	if (positioned.length === 0) {
		return onPage.map((label) => label.questionNumber);
	}
	const figureTop = figure.y + figure.height;
	const above = positioned.filter((label) => label.y >= figureTop - LABEL_SLACK);
	const pool = above.length > 0 ? above : positioned;
	const chosen = pool.reduce((nearest, label) => (label.y < nearest.y ? label : nearest));
	return [chosen.questionNumber];
};

/** Group figures by question. Each list runs from the top of the page downward. */
export const groupFiguresByQuestion = (
	figures: readonly ClusteredFigure[],
	labels: readonly FigureLabel[],
): Map<string, ClusteredFigure[]> => {
	const grouped = new Map<string, ClusteredFigure[]>();
	for (const figure of figures) {
		for (const questionNumber of questionNumbersFor(figure, labels)) {
			const list = grouped.get(questionNumber) ?? [];
			list.push(figure);
			grouped.set(questionNumber, list);
		}
	}
	for (const list of grouped.values()) {
		list.sort((left, right) => right.y - left.y || left.x - right.x);
	}
	return grouped;
};

const CAPTION_KIND: Record<string, string> = {
	image: "image",
	images: "image",
	delwedd: "image",
	delweddau: "image",
	llun: "image",
	lluniau: "image",
	graph: "graph",
	graphs: "graph",
	graff: "graph",
	graffiau: "graph",
	table: "table",
	tables: "table",
	tabl: "table",
	tablau: "table",
	diagram: "diagram",
	diagrams: "diagram",
	diagramau: "diagram",
	figure: "figure",
	figures: "figure",
	ffigur: "figure",
	ffigurau: "figure",
	chart: "chart",
	charts: "chart",
	siart: "chart",
	siartiau: "chart",
};

/** Caption words the exact-caption test accepts. The longest word is tried first. */
export const CAPTION_WORD_LIST: readonly string[] = Object.keys(CAPTION_KIND);

const CAPTION_WORDS = [...CAPTION_WORD_LIST].sort((left, right) => right.length - left.length).join("|");

const EXACT_CAPTION = new RegExp(`^(?:${CAPTION_WORDS})\\s+\\d+(?:\\.\\d+)?$`, "i");

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const captionPattern = (words: readonly string[]): RegExp => {
	const body = [...words]
		.map((word) => word.trim())
		.filter((word) => word.length > 0)
		.sort((left, right) => right.length - left.length)
		.map((word) => escapeRegExp(word))
		.join("|");
	return new RegExp(`^(?:${body})\\s+\\d+(?:\\.\\d+)?$`, "i");
};

/** The whole run is a caption, such as "Image 1.2" or "Figure 1". */
export const isExactAssetCaption = (text: string, words?: readonly string[]): boolean => {
	const source = text.trim();
	if (!words) {
		return EXACT_CAPTION.test(source);
	}
	const trimmed = words.map((word) => word.trim()).filter((word) => word.length > 0);
	if (trimmed.length === 0) {
		return false;
	}
	return captionPattern(trimmed).test(source);
};

const captionId = (word: string, number: string): string =>
	`${CAPTION_KIND[word.toLowerCase()] ?? word.toLowerCase()} ${number}`;

const captionIdsInText = (text: string): string[] => {
	const pattern = new RegExp(
		`\\b(${CAPTION_WORDS})\\s+(\\d+(?:\\.\\d+)?)(?:\\s+(?:and|a)\\s+(\\d+(?:\\.\\d+)?))?`,
		"gi",
	);
	const ids: string[] = [];
	for (const match of text.matchAll(pattern)) {
		const word = match[1];
		const first = match[2];
		const second = match[3];
		if (!word || !first) {
			continue;
		}
		ids.push(captionId(word, first));
		if (second) {
			ids.push(captionId(word, second));
		}
	}
	return ids;
};

const anchorCaptionId = (text: string): string | undefined => {
	const match = new RegExp(`^(${CAPTION_WORDS})\\s+(\\d+(?:\\.\\d+)?)\\b`, "i").exec(text.trim());
	if (!match?.[1] || !match[2]) {
		return undefined;
	}
	return captionId(match[1], match[2]);
};

const verticalGap = (figure: ClusteredFigure, y: number): number => {
	const top = figure.y + figure.height;
	if (y > top) {
		return y - top;
	}
	if (y < figure.y) {
		return figure.y - y;
	}
	return 0;
};

const captionIdsForFigure = (
	figure: ClusteredFigure,
	items: readonly { text: string; x: number; y: number }[],
): string[] => {
	const anchors: { id: string; x: number }[] = [];
	for (const item of items) {
		const id = anchorCaptionId(item.text);
		if (!id || verticalGap(figure, item.y) > CAPTION_SLACK) {
			continue;
		}
		anchors.push({ id, x: item.x });
	}
	const covering = anchors.filter(
		(anchor) => anchor.x >= figure.x - CAPTION_X_BEFORE && anchor.x <= figure.x + figure.width + CAPTION_X_AFTER,
	);
	const chosen =
		covering.length > 0
			? covering
			: anchors.length === 0
				? []
				: [
						anchors.reduce((nearest, anchor) =>
							Math.abs(anchor.x - figure.x) < Math.abs(nearest.x - figure.x) ? anchor : nearest,
						),
					];
	return [...new Set(chosen.map((anchor) => anchor.id))];
};

/** An item may carry a width, so the inline-reference filter can see body text. */
interface CaptionItem {
	text: string;
	x: number;
	y: number;
	width?: number;
}

/** Items on the same baseline sit within this many points of each other. */
const CAPTION_LINE_TOL = 4;
/** A run to the left closer than this does not count as separate body text. */
const CAPTION_LEFT_CLEAR = 6;

/**
 * A caption band: one caption line and the ids it owns. A band owns the page
 * from its own baseline down to the next band. Several captions on one line
 * (side-by-side "Graph 4.1" and "Graph 4.2", or a "Graphs 4.1 and 4.2" pair)
 * share a band, so the figures under them merge into one asset.
 */
interface CaptionBand {
	ids: Set<string>;
	top: number;
}

/**
 * Real caption anchors on a page, in page order. A caption is real only when it
 * leads its line: no text run starts to its left on the same baseline. An
 * inline reference inside a sentence ("... shown in Image 9.1") has body text
 * to its left, so it is not a caption and does not own a band.
 */
const realCaptionAnchors = (items: readonly CaptionItem[]): { id: string; y: number }[] => {
	const anchors: { id: string; y: number }[] = [];
	for (const item of items) {
		const id = anchorCaptionId(item.text);
		if (!id) {
			continue;
		}
		const hasTextLeft = items.some(
			(other) =>
				other !== item &&
				Math.abs(other.y - item.y) <= CAPTION_LINE_TOL &&
				other.x < item.x &&
				other.x + (other.width ?? 0) <= item.x + CAPTION_LEFT_CLEAR,
		);
		if (hasTextLeft) {
			continue;
		}
		anchors.push({ id, y: item.y });
	}
	return anchors;
};

/** Caption bands top-to-bottom. Captions sharing a baseline form one band. */
const captionBands = (items: readonly CaptionItem[]): CaptionBand[] => {
	const anchors = realCaptionAnchors(items).sort((left, right) => right.y - left.y);
	const bands: CaptionBand[] = [];
	for (const anchor of anchors) {
		const last = bands[bands.length - 1];
		if (last && Math.abs(last.top - anchor.y) <= CAPTION_LINE_TOL) {
			last.ids.add(anchor.id);
			continue;
		}
		bands.push({ ids: new Set([anchor.id]), top: anchor.y });
	}
	return bands;
};

/**
 * The caption band that owns a figure: the lowest band whose caption baseline
 * still sits at or above the figure's vertical centre. A figure above every
 * caption, or on a page with no caption, belongs to no band.
 */
const bandForFigure = (figure: ClusteredFigure, bands: readonly CaptionBand[]): CaptionBand | undefined => {
	const centre = figure.y + figure.height / 2;
	let owner: CaptionBand | undefined;
	for (const band of bands) {
		if (band.top >= centre) {
			owner = band;
		} else {
			break;
		}
	}
	return owner;
};

/** The inner markup of an SVG string, without its outer `<svg ...>` wrapper. */
const svgBody = (svg: string): string => svg.replace(/^<svg\b[^>]*>/, "").replace(/<\/svg>\s*$/, "");

/**
 * Composite several figures into one SVG whose viewBox covers them all.
 * Each fragment keeps its own local coordinates; a translate places it at its
 * offset from the merged box's top-left. The page y axis points up, so the SVG
 * y offset is measured from the top of the merged box down to the top of the
 * fragment.
 */
const compositeFigureSvg = (figures: readonly ClusteredFigure[], box: FigureBox): string => {
	const mergedTop = box.y + box.height;
	const groups = figures
		.map((figure) => {
			const dx = figure.x - box.x;
			const dy = mergedTop - (figure.y + figure.height);
			return `<g transform="translate(${formatNumber(dx)} ${formatNumber(dy)})">${svgBody(figure.svg)}</g>`;
		})
		.join("");
	const width = formatNumber(box.width);
	const height = formatNumber(box.height);
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${groups}</svg>`;
};

/**
 * A caption band index for a set of pages. `keyOf` gives the band key a figure
 * or part falls in — `page|ids` — or undefined when it is under no caption. Two
 * drawings share a band when they share a key.
 */
const captionBandIndex = (
	pages: readonly { pageNumber: number; items: readonly CaptionItem[] }[],
): { keyOf: (figure: ClusteredFigure) => string | undefined } => {
	const bandsByPage = new Map<number, CaptionBand[]>();
	const bandsFor = (page: number): CaptionBand[] => {
		const cached = bandsByPage.get(page);
		if (cached) {
			return cached;
		}
		const items = pages.find((candidate) => candidate.pageNumber === page)?.items ?? [];
		const bands = captionBands(items);
		bandsByPage.set(page, bands);
		return bands;
	};
	return {
		keyOf: (figure) => {
			const band = bandForFigure(figure, bandsFor(figure.page));
			if (!band) {
				return undefined;
			}
			return `${figure.page}|${[...band.ids].sort().join(",")}`;
		},
	};
};

const figureBoxOf = (figure: ClusteredFigure): FigureBox => ({
	x: figure.x,
	y: figure.y,
	width: figure.width,
	height: figure.height,
});

/**
 * Fold each undersized part back into the one figure it belongs to.
 *
 * A part joins a figure only when both fall in the same caption band and the
 * part sits within {@link PART_REJOIN_GAP} of that figure — the caption proves
 * they are one figure, and the gap keeps page furniture out. A part in no band,
 * in a band with no figure, or far from every figure, is dropped.
 *
 * Unlike {@link mergeFiguresByCaption}, this never merges two full figures: it
 * only repairs a figure that clustering split, so a caller that groups by label
 * (the mark-scheme path) keeps its own grouping while sharing this one rescue.
 * A part joins the nearest qualifying figure, so it is counted once.
 */
export const rejoinFigureParts = (
	figures: readonly ClusteredFigure[],
	pages: readonly { pageNumber: number; items: readonly CaptionItem[] }[],
	parts: readonly ClusteredFigure[] = [],
): ClusteredFigure[] => {
	if (parts.length === 0) {
		return [...figures];
	}
	const { keyOf } = captionBandIndex(pages);
	const extra = new Map<ClusteredFigure, ClusteredFigure[]>();
	const maxGap = PART_REJOIN_GAP * PART_REJOIN_GAP;
	for (const part of parts) {
		const key = keyOf(part);
		if (!key) {
			continue;
		}
		const partBox = figureBoxOf(part);
		let chosen: ClusteredFigure | undefined;
		let best = maxGap;
		for (const figure of figures) {
			if (keyOf(figure) !== key) {
				continue;
			}
			const gap = boxGap(figureBoxOf(figure), partBox);
			if (gap <= best) {
				best = gap;
				chosen = figure;
			}
		}
		if (chosen) {
			const list = extra.get(chosen) ?? [];
			list.push(part);
			extra.set(chosen, list);
		}
	}
	return figures.map((figure) => {
		const fragments = extra.get(figure);
		if (!fragments || fragments.length === 0) {
			return figure;
		}
		const all = [figure, ...fragments];
		const box = all.slice(1).reduce((union, item) => unionBox(union, figureBoxOf(item)), figureBoxOf(figure));
		const ordered = [...all].sort((left, right) => right.y - left.y || left.x - right.x);
		return {
			page: figure.page,
			x: box.x,
			y: box.y,
			width: box.width,
			height: box.height,
			svg: compositeFigureSvg(ordered, box),
		};
	});
};

/**
 * Merge figure fragments that share a caption into one asset.
 *
 * A single captioned image — "Image 9.1", "Image 9.3", or the pair
 * "Graphs 4.1 and 4.2" — often reaches us as several clustered fragments
 * (side-by-side charts, flasks in a row, parts of one diagram). The caption
 * above them is what makes them one figure, not their geometry.
 *
 * Each page is read as caption bands: a real caption owns the page from its own
 * baseline down to the next caption. A fragment joins the band above its
 * centre, and fragments in one band merge. This never chains a whole page
 * together — a band is bounded by the next caption — and it keeps the
 * "Graphs 4.1 and 4.2" pair as one asset, because both captions sit on one line
 * and form one band.
 *
 * A fragment under no caption keeps its own identity. Fragments merge only
 * within a page. The merged figure's SVG composites the fragments in their page
 * positions, and its box is their union.
 *
 * `parts` are undersized clusters from {@link clusterFigurePageParts}. A part
 * joins a figure only when it falls in a caption band that already owns a real
 * figure — the caption is the proof that the part belongs to that figure. A
 * part in no band, or in a band with no real figure, is dropped. So a reaction
 * product that a 2pt gap split off from "Image 9.1" rejoins it, but a stray
 * mark on a captionless page never becomes an asset.
 */
export const mergeFiguresByCaption = (
	figures: readonly ClusteredFigure[],
	pages: readonly { pageNumber: number; items: readonly CaptionItem[] }[],
	parts: readonly ClusteredFigure[] = [],
): ClusteredFigure[] => {
	// Repair figures that clustering split, then merge figures that share a
	// caption band. The rescue runs first, so a part is folded into its figure
	// before the band-merge groups figures together.
	const repaired = rejoinFigureParts(figures, pages, parts);
	const { keyOf } = captionBandIndex(pages);
	// Bucket by "page|band ids". A figure under no caption band gets a unique
	// key so it never merges with another.
	const buckets = new Map<string, ClusteredFigure[]>();
	let loneKey = 0;
	for (const figure of repaired) {
		const key = keyOf(figure);
		if (!key) {
			buckets.set(`lone-${loneKey}`, [figure]);
			loneKey += 1;
			continue;
		}
		const bucket = buckets.get(key) ?? [];
		bucket.push(figure);
		buckets.set(key, bucket);
	}
	const merged: ClusteredFigure[] = [];
	for (const bucket of buckets.values()) {
		if (bucket.length === 1) {
			merged.push(bucket[0]);
			continue;
		}
		const box = bucket
			.slice(1)
			.reduce(
				(union, figure) => unionBox(union, { x: figure.x, y: figure.y, width: figure.width, height: figure.height }),
				{ x: bucket[0].x, y: bucket[0].y, width: bucket[0].width, height: bucket[0].height },
			);
		const ordered = [...bucket].sort((left, right) => right.y - left.y || left.x - right.x);
		merged.push({
			page: bucket[0].page,
			x: box.x,
			y: box.y,
			width: box.width,
			height: box.height,
			svg: compositeFigureSvg(ordered, box),
		});
	}
	return merged.sort((left, right) => left.page - right.page || right.y - left.y || left.x - right.x);
};

export interface PaperFigureQuestion {
	questionNumber: string;
	context?: readonly string[];
	text: string;
}

/**
 * Give a captioned figure to every question that names it.
 * A figure with no caption still uses the label above it.
 */
export const groupPaperFigures = (
	figures: readonly ClusteredFigure[],
	questions: readonly PaperFigureQuestion[],
	pages: readonly { pageNumber: number; items: readonly CaptionItem[] }[],
	labels: readonly FigureLabel[],
	parts: readonly ClusteredFigure[] = [],
): Map<string, ClusteredFigure[]> => {
	const questionsByCaption = new Map<string, string[]>();
	for (const question of questions) {
		const blob = [...(question.context ?? []), question.text].join(" ");
		for (const id of captionIdsInText(blob)) {
			const list = questionsByCaption.get(id) ?? [];
			if (!list.includes(question.questionNumber)) {
				list.push(question.questionNumber);
			}
			questionsByCaption.set(id, list);
		}
	}

	const grouped = new Map<string, ClusteredFigure[]>();
	const add = (questionNumber: string, figure: ClusteredFigure): void => {
		const list = grouped.get(questionNumber) ?? [];
		const isAlreadyStored = list.some(
			(item) => item.svg === figure.svg && item.page === figure.page && item.y === figure.y,
		);
		if (isAlreadyStored) {
			return;
		}
		list.push(figure);
		grouped.set(questionNumber, list);
	};

	const mergedFigures = mergeFiguresByCaption(figures, pages, parts);
	for (const figure of mergedFigures) {
		const items = pages.find((page) => page.pageNumber === figure.page)?.items ?? [];
		const ids = captionIdsForFigure(figure, items);
		const named = [...new Set(ids.flatMap((id) => questionsByCaption.get(id) ?? []))];
		const targets = named.length > 0 ? named : questionNumbersFor(figure, labels);
		for (const questionNumber of targets) {
			add(questionNumber, figure);
		}
	}

	for (const list of grouped.values()) {
		list.sort((left, right) => right.y - left.y || left.x - right.x);
	}
	return grouped;
};

const indexKeys = (keys: ReadonlyMap<string, readonly FigureAssetKeys[]>): Map<string, readonly FigureAssetKeys[]> => {
	const indexed = new Map<string, readonly FigureAssetKeys[]>();
	for (const [questionNumber, values] of keys) {
		const key = normaliseAssetNumber(questionNumber);
		if (!indexed.has(key)) {
			indexed.set(key, values);
		}
	}
	return indexed;
};

const pairSlots = (english: readonly FigureAssetKeys[], welsh: readonly FigureAssetKeys[]): AssetRef[] => {
	const slots: AssetRef[] = [];
	const count = Math.max(english.length, welsh.length);
	for (let index = 0; index < count; index += 1) {
		const slot: AssetRef = {};
		if (english[index]?.svg) {
			slot.en = english[index].svg;
		}
		if (english[index]?.raster) {
			slot.enRaster = english[index].raster;
		}
		if (welsh[index]?.svg) {
			slot.cy = welsh[index].svg;
		}
		if (welsh[index]?.raster) {
			slot.cyRaster = welsh[index].raster;
		}
		if (slot.en || slot.cy) {
			slots.push(slot);
		}
	}
	return slots;
};

/** Pair English and Welsh keys in figure order. The manifest omits a question with no asset. */
export const buildAssetManifest = (input: {
	order: readonly string[];
	paperEn: ReadonlyMap<string, readonly FigureAssetKeys[]>;
	paperCy: ReadonlyMap<string, readonly FigureAssetKeys[]>;
	markSchemeEn: ReadonlyMap<string, readonly FigureAssetKeys[]>;
	markSchemeCy: ReadonlyMap<string, readonly FigureAssetKeys[]>;
}): AssetManifest => {
	const paperEn = indexKeys(input.paperEn);
	const paperCy = indexKeys(input.paperCy);
	const markSchemeEn = indexKeys(input.markSchemeEn);
	const markSchemeCy = indexKeys(input.markSchemeCy);
	const seen = new Set<string>();
	const numbers: string[] = [];
	const add = (questionNumber: string) => {
		const key = normaliseAssetNumber(questionNumber);
		if (key.length === 0 || seen.has(key)) {
			return;
		}
		seen.add(key);
		numbers.push(questionNumber);
	};
	for (const questionNumber of input.order) {
		add(questionNumber);
	}
	for (const questionNumber of [
		...input.paperEn.keys(),
		...input.paperCy.keys(),
		...input.markSchemeEn.keys(),
		...input.markSchemeCy.keys(),
	]) {
		add(questionNumber);
	}

	const questions: AssetManifest["questions"] = [];
	for (const questionNumber of numbers) {
		const key = normaliseAssetNumber(questionNumber);
		const paper = pairSlots(paperEn.get(key) ?? [], paperCy.get(key) ?? []);
		const markScheme = pairSlots(markSchemeEn.get(key) ?? [], markSchemeCy.get(key) ?? []);
		if (paper.length === 0 && markScheme.length === 0) {
			continue;
		}
		questions.push({ questionNumber, paper, markScheme });
	}
	return { questions };
};

/**
 * Place each mark-scheme question on its extracted page.
 * A text item that is the question number supplies y. A missing item leaves y null.
 */
export const markSchemeFigureLabels = (
	questions: readonly { questionNumber: string; page: number }[],
	pages: readonly { pageNumber: number; items: readonly { text: string; y: number }[] }[],
): FigureLabel[] => {
	const original = new Map<string, string>();
	const labels = new Map<string, FigureLabel>();
	for (const question of questions) {
		const key = normaliseAssetNumber(question.questionNumber);
		if (!original.has(key)) {
			original.set(key, question.questionNumber);
		}
		const id = `${question.page}:${key}`;
		if (!labels.has(id)) {
			labels.set(id, { questionNumber: question.questionNumber, page: question.page, y: null });
		}
	}
	for (const page of pages) {
		for (const item of page.items) {
			const key = normaliseAssetNumber(item.text);
			const questionNumber = original.get(key);
			if (!questionNumber) {
				continue;
			}
			const id = `${page.pageNumber}:${key}`;
			const existing = labels.get(id);
			if (!existing || existing.y === null || item.y > existing.y) {
				labels.set(id, { questionNumber, page: page.pageNumber, y: item.y });
			}
		}
	}
	return [...labels.values()];
};
